import React, { useRef } from 'react';
import { BodyText, LoraText } from '@/shared/components/StyledText';
import { green, sand, welcomeMutedOnGreen } from '@/shared/styles/colors';
import styled from '@emotion/native';
import { ScrollView, View } from 'react-native';
import { useAuth } from '@/services/auth/AuthProvider';
import OnboardingHeader from '@/shared/components/navigation/OnboardingHeader';
import useDimensions from '@/shared/hooks/useDimensions';
import Carousel from '@/shared/components/carousel/Carousel';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { IsDesktopProps } from '@/shared/styles/types';
import { IS_WEB } from '@/shared/constants/utility';
import {
  FONT_STYLES,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
} from '@/shared/styles/constants';
import DevMenuButton from './components/DevMenuButton';
import HowEmmeWorksSection from './components/HowEmmeWorksSection';
import WelcomeVideoSection from './components/WelcomeVideoSection';
import WelcomeStylistsCalloutSection from './components/WelcomeStylistsCalloutSection';
import WelcomeClientTestimonialsSection from './components/WelcomeClientTestimonialsSection';
import WelcomeAboutFounderSection from './components/WelcomeAboutFounderSection';
import WelcomeKeepInTouchSection from './components/WelcomeKeepInTouchSection';
import WelcomeQuestionsFaqSection from './components/WelcomeQuestionsFaqSection';
import Footer from '@/shared/components/Footer';
import SentryService from '@/services/sentry/Sentry.service';
import WelcomeButton from '@/components/welcome/WelcomeButton';

import CarouselImageOne from '@/assets/images/CarouselImageOne.png';
import CarouselImageTwo from '@/assets/images/CarouselImageTwo.png';
import CarouselImageThree from '@/assets/images/CarouselImageThree.png';
import CarouselImageFour from '@/assets/images/CarouselImageFour.png';
import CarouselImageFive from '@/assets/images/CarouselImageFive.png';
import CarouselImageSix from '@/assets/images/CarouselImageSix.png';
import CarouselImageSeven from '@/assets/images/CarouselImageSeven.png';
import CarouselImageEight from '@/assets/images/CarouselImageEight.png';
import CarouselImageNine from '@/assets/images/CarouselImageNine.png';
import CarouselImageTen from '@/assets/images/CarouselImageTen.png';
import CarouselImageEleven from '@/assets/images/CarouselImageEleven.png';
import CarouselImageTwelve from '@/assets/images/CarouselImageTwelve.png';
import CarouselImageThirteen from '@/assets/images/CarouselImageThirteen.png';

const WELCOME_HEADLINE_SINGLE_LINE_MIN_WIDTH = 680;

interface WelcomeHeadlineProps extends IsDesktopProps {
  isWideHeadline?: boolean;
}

const imageArray = [
  CarouselImageOne,
  CarouselImageTwo,
  CarouselImageThree,
  CarouselImageFour,
  CarouselImageFive,
  CarouselImageSix,
  CarouselImageSeven,
  CarouselImageEight,
  CarouselImageNine,
  CarouselImageTen,
  CarouselImageEleven,
  CarouselImageTwelve,
  CarouselImageThirteen,
];

const Welcome = () => {
  const { width } = useDimensions();
  const isDesktop = useIsDesktop();
  const scrollRef = useRef<ScrollView>(null);

  const { logIn } = useAuth();

  const onStylistLogin = async () => {
    try {
      await logIn();
    } catch (error) {
      SentryService.captureError('Failed to log in:', error);
    }
  };

  const carouselItemWidth = isDesktop ? 330 : 176;
  const carouselItemHeight = isDesktop ? 478 : 300;
  const isWideHeadline = width >= WELCOME_HEADLINE_SINGLE_LINE_MIN_WIDTH;

  return (
    <OuterContainer ref={scrollRef} contentContainerStyle={{ flexGrow: 1 }}>
      <OnboardingHeader
        onPress={onStylistLogin}
        authText="Log In"
        hideBackButton
      />
      <HeaderSpacer />
      <ContentContainer isDesktop={isDesktop}>
        <SecondaryContainer>
          <StyledH2 isDesktop={isDesktop} isWideHeadline={isWideHeadline}>
            expert stylists, on-demand
          </StyledH2>
          <StyledBody isDesktop={isDesktop}>
            {isDesktop
              ? 'One-on-one video and in-person consults with top fashion experts—ready when you are, wherever you are.'
              : 'One-on-one video and in-person consults with top fashion experts—ready when you are,\nwherever you are.'}
          </StyledBody>
          <WelcomeButton />
        </SecondaryContainer>
        <CarouselAndStepsBlock>
          <Carousel
            containerStyle={{ marginBottom: 116, paddingTop: 50 }}
            maxWidth={width}
            loop
            autoplay
            itemWidth={carouselItemWidth + 13}
            itemHeight={carouselItemHeight}
          >
            {imageArray.map((image, index) => (
              <StyledImage
                key={index}
                source={image}
                height={carouselItemHeight}
                width={carouselItemWidth}
              />
            ))}
          </Carousel>
          <HowEmmeWorksAnchor>
            <HowEmmeWorksSection />
          </HowEmmeWorksAnchor>
          <WelcomeVideoSection />
          <WelcomeStylistsCalloutSection />
          <WelcomeClientTestimonialsSection />
        </CarouselAndStepsBlock>
      </ContentContainer>
      <WelcomeAboutFounderSection />
      <WelcomeKeepInTouchSection />
      <WelcomeQuestionsFaqSection />
      {isDesktop ? <Spacer /> : null}
      <Footer />
      {!IS_WEB && <DevMenuButton />}
    </OuterContainer>
  );
};

const StyledImage = styled.Image(({ height, width }) => ({
  height,
  width,
  borderRadius: 9,
  marginHorizontal: 7,
}));

const OuterContainer = styled.ScrollView({
  backgroundColor: green,
  flex: 1,
});

const ContentContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: isDesktop ? 'space-between' : 'flex-start',
}));

const CarouselAndStepsBlock = styled.View({
  width: '100%',
  alignItems: 'center',
});

const HowEmmeWorksAnchor = styled(View)({
  width: '100%',
  alignItems: 'center',
});

const StyledH2 = styled(LoraText)<WelcomeHeadlineProps>(
  ({ isDesktop, isWideHeadline }) => ({
    color: sand,
    ...(isWideHeadline
      ? {
          alignSelf: 'stretch',
          ...(IS_WEB ? { whiteSpace: 'nowrap' } : {}),
        }
      : { maxWidth: 300 }),
    textAlign: 'center',
    textTransform: 'uppercase',
    marginTop: 80,
    marginBottom: 7,
    fontSize: isDesktop ? 45 : 40,
    lineHeight: isDesktop ? 40 : 38,
    letterSpacing: 0.68,
    fontWeight: 'normal',
  }),
);

const StyledBody = styled(BodyText)<IsDesktopProps>(({ isDesktop }) => ({
  ...FONT_STYLES.welcomeHelveticaNeue,
  color: welcomeMutedOnGreen,
  textAlign: 'center',
  marginTop: 15,
  marginBottom: isDesktop ? 24 : 32,
  fontSize: 16,
  lineHeight: 21.8,
  letterSpacing: 0.9,
}));

const SecondaryContainer = styled.View({
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  paddingHorizontal: WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
});

const Spacer = styled.View({
  flex: 1,
});

const HeaderSpacer = styled.View({
  height: 48,
  width: '100%',
});

export default Welcome;
